import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import QuestProprietaryDataIcon from "@app/overviews/network-benchmarking-dashboard/filters/QuestProprietaryDataIcon";
import { CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsTabTypeEnum";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import MedicareNetworkFlyout from "./MedicareNetworkFlyout";
import { MedicareNetwork } from "./shared/MedicareNetwork";
import { CompetitiveInsightsCountyFilterTypeEnum } from "../../filters/shared/CompetitiveInsightsCountyFilterType";

interface MedicareNetworkFiltersTableProps {
  colorMap?: Map<string, string>;
  data: MedicareNetwork[];
  isMapView?: boolean;
  isNetworkSummary?: boolean;
  parentOrganizationName: string;
  selectedContractIds: string[];
}

interface MedicareNetworkFiltersTableData {
  contractName: JSX.Element;
  countyCount: string;
  membership: string;
  opportunityCountyCount: string;
  rating: string;
  rowIsDisabled?: boolean;
  type: string;
  [wafflesKeyColumnName]: string;
}

const MedicareNetworkFiltersTable = (props: MedicareNetworkFiltersTableProps): JSX.Element => {
  const [showingMore, setShowingMore] = useState<boolean>(false);
  const [tableData, setTableData] = useState<MedicareNetworkFiltersTableData[]>([]);
  //const [updateData, setUpdateData] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  useEffect(() => {
    // on load, if there are selected contracts (from a previous search, or from applying service area),
    // check to see if they are in the "load more" part of a table (if so, open it!)
    if (cloneDeep(props.data).slice(3).map(x => x.contractId).some(y => props.selectedContractIds.includes(y))) {
      if (!showingMore) {
        setShowingMore(true);
      }
    } else {
      if (showingMore) {
        setShowingMore(false);
      }
    }
  }, [props.data]);

  const handleFlyoutOpen = (): void => {
    if (benchmarkingFiltersContext.selectedTab === CompetitiveInsightsTabTypeEnum.NetworkSelection) {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  useEffect(() => {
    if (!!props.data) {
      /* oxlint-disable sort-keys */
      const end = props.isNetworkSummary ? 5 : 3;
      const newTableData: MedicareNetworkFiltersTableData[] = (showingMore ? props.data : props.data.slice(0, end)).map((contract, index) => {
        return {
          [wafflesKeyColumnName]: contract.contractId || index.toString(),
          contractName: (
            <>
              <FlyoutLink
                classNames="qes-button qes-button-secondary h-contract-flyout-link-btn"
                testId={`network-benchmarking-dashboard--network-filters__flyout-link-${contract.contractId}`}
                onOpen={handleFlyoutOpen}
                content={{
                  body: (
                    <MedicareNetworkFlyout
                      contract={contract}
                    />
                  ),
                  title: `Contract ${contract.contractId}`,
                }}
              >
                {props.isNetworkSummary && (
                  <QesIcon
                    style={{ color: props.colorMap?.get(contract.contractId), fill: props.colorMap?.get(contract.contractId) }}
                    iconName={FontAwesome.Square}
                    width="25"
                    className="me-3"
                    description="Open network flyout"
                  />
                )}
                <span className="h-contract-flyout-link">
                  {contract.contractId}
                </span>
              </FlyoutLink>
              {contract.isQesNetwork && (
                <QuestProprietaryDataIcon />
              )}
              {contract.isSnpPlan && (
                <TooltipWrapper
                  containerClassName="ms-1"
                  text="Contract contains special needs plan"
                >
                  <img className="d-block" src={`${window.customUIBaseUrl}/images/snp-icon.svg`} alt="" />
                </TooltipWrapper>
              )}
            </>
          ),
          type: getPlanTypeShortenedName(contract.planType),
          membership: printNumberAsString(contract.membership, "---"),
          countyCount: printNumberAsString(contract.countyCount, "---"),
          opportunityCountyCount: printNumberAsString(contract.opportunityCountyCount, "---"),
          rating: printNumberAsString(contract.rating, "---"),
          // disable row if contract is disabled (due to lack of data) OR
          // five contracts have already been selected and the row isn't one of the selected h contracts
          rowIsDisabled: contract.disabled || (
            benchmarkingFiltersContext.selectedNetworks.length === 5 && !benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId).includes(contract.contractId)
          ),
        }
      });
      /* oxlint-enable sort-keys */

      setTableData(newTableData);
    }
  }, [props.data, benchmarkingFiltersContext.selectedNetworks, showingMore]);

  const columnConfig: ColumnConfig = {
    contractName: {
      alignment: TextAlignment.Left,
      displayText: "",
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "contract-name",
    },
    countyCount: {
      alignment: TextAlignment.Center,
      displayElement: <>{props.parentOrganizationName === "Your QES Networks" ? "Current" : "Active"}<br />County Count</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "county-count",
    },
    membership: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Membership</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "membership",
    },
    opportunityCountyCount: {
      alignment: TextAlignment.Center,
      displayElement: <>Opportunity<br />County Count</>,
      hidden: props.parentOrganizationName !== "Your QES Networks",
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "opportunity-county-count",
    },
    rating: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Rating</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "rating",
    },
    type: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Type</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "plan-type",
    },
  };

  const getPlanTypeShortenedName = (planType: string): string => {
    if (!planType) {
      return "";
    } else if (planType.toUpperCase().includes("MEDICARE-MEDICAID")) {
      return "MMP";
    } else if (planType.toUpperCase().includes("1876 COST")) {
      return "1876";
    } else if (planType.toUpperCase().includes("HMO/HMOPOS")) {
      return "HMO";
    } else if (planType.toUpperCase().includes("LOCAL PPO") || planType.toUpperCase().includes("REGIONAL PPO")) {
      return "PPO";
    } else {
      return planType;
    }
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    // obtain the h contract models based off their contract ids
    const selectedHContracts: MedicareNetwork[] = cloneDeep(props.data).filter(
      (x) => selectedKeys.includes(x.contractId.toString())
    );

    // since toggling a waffles checkbox returns all of the currently-selected keys
    // and the selected h contracts array can include h contracts from different parent organizations,
    // filter out all h contracts that correspond to this parent organization from the select h contracts...
    const newSelectedHContracts: MedicareNetwork[] = cloneDeep(benchmarkingFiltersContext.selectedNetworks as MedicareNetwork[]).filter(
      (x) => !props.data.map(d => d.contractId).includes(x.contractId)
    );

    // ...and then add back the selected h contracts for this parent organization
    benchmarkingFiltersContext.setSelectedNetworks(newSelectedHContracts.concat(selectedHContracts));
  }

  const onRowMouseOver = (row: MedicareNetworkFiltersTableData): void => {
    if (!!row) {
      const network: MedicareNetwork = props.data.find(x => x.contractId === row[wafflesKeyColumnName]);

      if (!!network) {
        benchmarkingFiltersContext.setMousedOverCountyIds(network.networkCounties.map(nc => nc.countyId.toString()));
        benchmarkingFiltersContext.setMousedOverNetwork(network);
      }
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
      benchmarkingFiltersContext.setMousedOverNetwork(null);
    }
  }

  return (
    <React.Fragment>
      {props.parentOrganizationName === "Your QES Networks" && props.isMapView && (
        <div className="d-flex flex-wrap">
          {Object.values(CompetitiveInsightsCountyFilterTypeEnum).map((filterType, index) => (
            <QESButton
              key={index}
              qesTestId={`network-benchmarking-dashboard__network-filters--county-chip-${filterType.toLowerCase()}`}
              qesType="none"
              className={`county-filter-type-pill me-2${benchmarkingFiltersContext.selectedCountyFilterType.includes(filterType) ? " county-filter-type-pill--active" : ""}`}
              onClick={() => benchmarkingFiltersContext.toggleSelectedCountyFilterType(filterType)}
            >
              {filterType}
            </QESButton>
          ))}
        </div>
      )}
      <Waffles<MedicareNetworkFiltersTableData>
        columns={columnConfig}
        data={tableData}
        disableStickyHeader={true}
        disableProcessingOverlay={true}
        fullWidth={true}
        handleCheckboxChange={handleCheckboxChange}
        keyColumns={["contractName"]}
        onRowMouseOver={onRowMouseOver}
        selectedKeys={benchmarkingFiltersContext.selectedNetworks.map((x) => x.contractId.toString())} //adjust so it's only for this tile
        showCheckboxes={!props.isNetworkSummary}
        testId={`network-benchmarking-dashboard--network-filters__table-${props.parentOrganizationName?.replace(/\s+/g, '-').toLowerCase()}`}
        theme={WafflesThemes.Simple}
      />
      {(props.data.length > (props.isNetworkSummary ? 5 : 3)) && (
        <QESButton
          qesTestId={`network-benchmarking-dashboard__network-filters--table-show-more-less-${props.parentOrganizationName?.replace(/\s+/g, '-').toLowerCase() }`}
          qesType="secondary"
          onClick={() => setShowingMore(!showingMore)}
          className="px-0 float-end"
        >
          Show {showingMore ? "Less" : "More"}
        </QESButton>
      )}
    </React.Fragment>

  )
}

export default MedicareNetworkFiltersTable;